// src/Pages/Collections/CollectionDetail.jsx
import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAPI } from '../../Contexts/APIContext'
import Loader from '../../Components/GeneralComponents/Loader'
import Collection from '../../Components/CollectionsComponents/Collection/Collection'
import { ModalWindow } from '../../Components/GeneralComponents/ModalWindow/ModalWindow'
import { ButtonOutline } from '../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import successToast from '../../Components/Toasts/SuccessToast'
import errorToast from '../../Components/Toasts/ErrorToast'
import classes from './collectionDetail.module.css'
import { Wrapper } from '../../Components/page/Wrapper'
import { Header } from '../../Components/page/Header'
import { Navigation } from '../../Components/page/Navigation'

const HOME_CAROUSEL_ID = 'TjDTJADsXAhXhGgykZ8u15xE21w8aNUg'
const DEFAULT_QUEUED_ID = 'qAdwEtUQwRmaXuJHDmHU8JwXnr5ynT1N'
const NEW_RELEASES_ID = 'UK3fZ8Up4e9PWnUMuMFfDmdyZwJW7j98'

export default function CollectionDetail() {
    const { id } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { getCollectionById, deleteCollection, updateCollection, getCollection } = useAPI()

    const [isPromptDelete, setIsPromptDelete] = useState(false)

    // Queued carousel state (only used when id === HOME_CAROUSEL_ID)
    const isHomeCarousel = id === HOME_CAROUSEL_ID
    const [queuedCollectionId, setQueuedCollectionId] = useState(DEFAULT_QUEUED_ID)
    const [queuedIdInput, setQueuedIdInput] = useState(DEFAULT_QUEUED_ID)
    const [showMoveModal, setShowMoveModal] = useState(false)
    const [isMoving, setIsMoving] = useState(false)

    // Debounce the queued collection ID input
    useEffect(() => {
        if (!isHomeCarousel) return
        const timer = setTimeout(() => {
            if (queuedIdInput.trim() && queuedIdInput.trim() !== queuedCollectionId) {
                setQueuedCollectionId(queuedIdInput.trim())
            }
        }, 500)
        return () => clearTimeout(timer)
    }, [queuedIdInput, isHomeCarousel])

    const { data, isLoading, isError } = useQuery({
        queryKey: ['collection', id],
        queryFn: () => getCollectionById(id),
    })

    // Fetch queued collection metadata
    const {
        data: queuedData,
        isLoading: queuedLoading,
        isError: queuedError,
    } = useQuery({
        queryKey: ['collection', queuedCollectionId],
        queryFn: () => getCollectionById(queuedCollectionId),
        enabled: isHomeCarousel && !!queuedCollectionId,
    })

    // Helper to fetch all feels from a collection using pagination
    const fetchAllFeels = useCallback(async (collectionFID) => {
        const pageLimit = 20
        let allFeels = []
        let page = 1
        let hasMore = true

        while (hasMore) {
            const response = await getCollection(collectionFID, page, pageLimit)
            const feels = response.feels || []
            allFeels.push(...feels)
            hasMore = page * pageLimit < (response.feels_count || 0)
            page++
        }

        return allFeels
    }, [getCollection])

    // Move mutation
    const moveMutation = useMutation({
        mutationFn: async ({ prepend }) => {
            // 1. Fetch queued feels
            const queuedFeels = await fetchAllFeels(queuedCollectionId)
            if (!queuedFeels.length) {
                throw new Error('Queued collection has no feels to move')
            }
            const queuedFIDs = queuedFeels.map(f => f.f_id)

            // 2. If prepend, fetch home carousel feels and new releases feels, combine
            if (prepend) {
                const homeFeels = await fetchAllFeels(HOME_CAROUSEL_ID)
                const newReleasesFeels = await fetchAllFeels(NEW_RELEASES_ID)

                const homeFIDs = homeFeels.map(f => f.f_id)
                const newReleasesFIDs = newReleasesFeels.map(f => f.f_id)
                const combinedFIDs = [...homeFIDs, ...newReleasesFIDs]

                // Fetch new releases metadata for the name
                const newReleasesData = await getCollectionById(NEW_RELEASES_ID)
                await updateCollection(
                    NEW_RELEASES_ID,
                    newReleasesData.name,
                    newReleasesData.is_public ?? true,
                    combinedFIDs
                )
            }

            // 3. Replace home carousel with queued feels
            await updateCollection(
                HOME_CAROUSEL_ID,
                data.name,
                data.is_public ?? true,
                queuedFIDs
            )
        },
        onSuccess: () => {
            successToast('Home carousel updated successfully')
            // Reload to reset zustand stores and refetch all collection data
            setTimeout(() => window.location.reload(), 500)
        },
        onError: (error) => {
            setIsMoving(false)
            errorToast(error?.message || 'Failed to move queued carousel')
        },
    })

    const handleMove = (prepend) => {
        setIsMoving(true)
        moveMutation.mutate({ prepend })
    }

    const breadcrumbs = [
        {
            title: 'All Collections',
            link: '/collections',
        },
        {
            title: data?.name || 'Collection',
        },
    ]

    if (isLoading) return <Loader isLoading />
    if (isError || !data?.f_id) return <h2>Collection not found.</h2>

    return (
        <Wrapper scrollY>
            <Header>
                <Navigation breadcrumbs={breadcrumbs} />
            </Header>

            <div className={classes.wrapper}>
                {/* Single collection display */}
                <div className={classes.singleCollection}>
                    <Collection
                        feelsCount={data.feels_count || 0}
                        name={data.name || 'Unnamed Collection'}
                        collectionId={data.f_id}
                        isPublic={data.is_public ?? false}
                        isExplicit={data.is_explicit}
                        keywords={data.keywords || []}
                        onDeleteCollection={() => setIsPromptDelete(true)}
                        dateUpdated={data.date_updated}
                    />
                </div>

                {/* Queued Carousel section — only for home carousel */}
                {isHomeCarousel && (
                    <>
                        <div className={classes.queuedSection}>
                            <div className={classes.queuedCard}>
                                <h2 className={classes.queuedTitle}>Queued Carousel</h2>
                                <div className={classes.queuedInputRow}>
                                    <input
                                        type="text"
                                        className={classes.queuedInput}
                                        value={queuedIdInput}
                                        onChange={(e) => setQueuedIdInput(e.target.value)}
                                        placeholder="Enter queued collection ID"
                                    />
                                </div>
                                {queuedData?.f_id && (
                                    <ButtonOutline
                                        color={'pink'}
                                        size="small"
                                        text={'Move Queued Carousel to Home Carousel'}
                                        onClick={() => setShowMoveModal(true)}
                                        disable={isMoving}
                                    />
                                )}
                            </div>

                            {queuedLoading && <Loader isLoading />}
                            {queuedError && (
                                <p className={classes.errorText}>
                                    Could not load queued collection. Check the ID.
                                </p>
                            )}

                            {queuedData?.f_id && (
                                <div className={classes.singleCollection}>
                                    <Collection
                                        feelsCount={queuedData.feels_count || 0}
                                        name={queuedData.name || 'Unnamed Collection'}
                                        collectionId={queuedData.f_id}
                                        isPublic={queuedData.is_public ?? false}
                                    />
                                </div>
                            )}
                        </div>

                        <ModalWindow
                            open={showMoveModal}
                            onClose={() => !isMoving && setShowMoveModal(false)}
                        >
                            <div className={classes.modalContent}>
                                <h3 className={classes.modalTitle}>
                                    Move Queued Carousel
                                </h3>
                                <p className={classes.modalText}>
                                    Should the current home carousel items be prepended to the
                                    &apos;New Releases&apos; collection?
                                </p>
                                <div className={classes.modalButtons}>
                                    <ButtonOutline
                                        color={'green'}
                                        size="small"
                                        text={'Yes, Prepend & Move'}
                                        onClick={() => handleMove(true)}
                                        disable={isMoving}
                                    />
                                    <ButtonOutline
                                        color={'pink'}
                                        size="small"
                                        text={'No, Just Move'}
                                        onClick={() => handleMove(false)}
                                        disable={isMoving}
                                    />
                                    <ButtonOutline
                                        color={'red'}
                                        size="small"
                                        text={'Cancel'}
                                        onClick={() => setShowMoveModal(false)}
                                        disable={isMoving}
                                    />
                                </div>
                                {isMoving && (
                                    <div className={classes.modalLoading}>
                                        <Loader isLoading />
                                    </div>
                                )}
                            </div>
                        </ModalWindow>
                    </>
                )}
            </div>
        </Wrapper>
    )
}
